import {
  createDrawerNavigator,
  DrawerContentComponentProps,
  DrawerContentScrollView,
  DrawerItemList,
  DrawerScreenProps,
} from '@react-navigation/drawer';
import { indexOf } from 'lodash';
import { ComponentType, useEffect, useState } from 'react';
import { Image, StatusBar, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { mvs } from 'react-native-size-matters';

import { DrawerFooter } from './DrawerFooter';
import PodcastStackNavigator from './PodcastStackNavigator';
import { getUrlFromImageName } from '../api/images';
import { useAppSelector } from '../app/hooks';
import { CustomDrawerIcon } from '../components/CustomDrawerIcon';
import { COLORS, DRAWER_SWIPE_EDGE_WIDTH, DRAWER_SWIPE_MIN_DISTANCE } from '../constants';
import {
  selectAppConfig,
  selectFontFamily,
  selectMenuBackgroundColor,
  selectMenuColor,
  selectMenuLogo,
  selectShowMenuLogo,
  selectTabs,
} from '../features/appconfig/store/selectors';
import { isLastIndex } from '../helpers/array';
import { getFontFamily } from '../helpers/fontMapper';
import { useAdjustedFontSize } from '../hooks/useAdjustedFontSize';
import useDrawerWidth from '../hooks/useDrawerWidth';
import { AppConfigTabs, TabType } from '../models';
import AlarmScreen from '../screens/AlarmScreen';
import AudioDedicationScreen from '../screens/AudioDedicationScreen';
import HtmlScreen from '../screens/HtmlScreen';
import LiveFeedScreen from '../screens/LiveFeedScreen';
import { PlayerScreen } from '../screens/PlayerScreen';
import RssScreen from '../screens/RSS/RssScreen';
import SettingsScreen from '../screens/SettingsScreen';
import SongHistoryScreen from '../screens/SongHistoryScreen';
import TextDedicationScreen from '../screens/TextDedicationScreen';
import WebsiteScreen from '../screens/WebsiteScreen';

interface MenuItem {
  name: string;
  component: ComponentType<any> | null; // FIXME Better typing, using LeftDrawerNavigatorScreenProps
  label: string;
  icon: string;
  options: AppConfigTabs[number];
  faIcon: string;
}

export type LeftDrawerNavigatorParams = {
  // FIXME Une solution temporaire a été faite pour utiliser des noms de routes dynamique plutôt que ce type pour éviter la contrainte d'unicité
  Player: undefined;
  Alarm: undefined;
  TextDedication: undefined;
  LiveFeed: undefined;
  Html: undefined;
  Website: undefined;
  Rss: undefined;
  AudioDedication: undefined;
  Settings: undefined;
  SongHistory: { options: AppConfigTabs[number] };
};

export type LeftDrawerNavigatorScreenProps<
  Screen extends keyof LeftDrawerNavigatorParams,
> = DrawerScreenProps<LeftDrawerNavigatorParams, Screen>;

const Drawer = createDrawerNavigator<LeftDrawerNavigatorParams>();

function CustomDrawerContent(props: DrawerContentComponentProps) {
  return (
    <SafeAreaView style={styles.safeAreaContainer} edges={['bottom']}>
      <StatusBar
        animated={true}
        barStyle={'default'}
        translucent={true}
        backgroundColor={COLORS.TRANSPARENT}
      />
      <View style={[{ backgroundColor: props.backgroundImageColor }, { flex: 1 }]}>
        <SafeAreaView style={styles.container} edges={['top']}>
          {props.menuLogo !== null && (
            <Image
              source={{ uri: props.menuLogo }}
              resizeMode="cover"
              style={styles.menuLogo}
            />
          )}
          <DrawerContentScrollView
            {...props}
            contentContainerStyle={styles.drawerContentContainer}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
          >
            <DrawerItemList {...props} />
          </DrawerContentScrollView>
          <DrawerFooter
            aboutVisibility={props.aboutVisibility}
            settingsActive={props.settingsActive}
            navigation={props.navigation}
          />
        </SafeAreaView>
      </View>
    </SafeAreaView>
  );
}

// FIXME Better typing, using LeftDrawerNavigatorScreenProps
export function convertTabTypeToScreen(tabType: TabType): ComponentType<any> | null {
  switch (tabType) {
    case TabType.Dedication:
      return TextDedicationScreen;
    case TabType.AudioDedication:
      return AudioDedicationScreen;
    case TabType.Html:
      return HtmlScreen;
    case TabType.LiveFeed:
      return LiveFeedScreen;
    case TabType.Radio:
      return PlayerScreen;
    case TabType.Alarm:
      return AlarmScreen;
    case TabType.Rss:
      return RssScreen;
    case TabType.Website:
      return WebsiteScreen;
    case TabType.Podcast:
      return PodcastStackNavigator;
    case TabType.SongHistory:
      return SongHistoryScreen;
    default:
      return null;
  }
}

export const LeftDrawerNavigator = () => {
  const tabs = useAppSelector(selectTabs);
  const appConfig = useAppSelector(selectAppConfig);
  const menuColor = useAppSelector(selectMenuColor); // FIXME Rassembler peut être ces 4 valeurs dans une seule méthode menuConfig
  const menuBackgroundColor = useAppSelector(selectMenuBackgroundColor);
  const menuLogo = useAppSelector(selectMenuLogo);
  const showMenuLogo = useAppSelector(selectShowMenuLogo);
  const fontFamily = useAppSelector(selectFontFamily);
  const drawerLabelFontSize = useAdjustedFontSize(mvs(18));

  const [menuItems, setMenuItems] = useState<MenuItem[]>(createMenuItem());

  function createMenuItem(): MenuItem[] {
    return tabs.map((tab, index) => ({
      name: 'tab' + index,
      component: convertTabTypeToScreen(tab.type),
      label: tab.titre,
      icon: getUrlFromImageName(appConfig, tab.icon),
      options: tab,
      faIcon: tab.faIcon,
    }));
  }

  useEffect(() => {
    setMenuItems(createMenuItem());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  return (
    <Drawer.Navigator
      initialRouteName="Player"
      backBehavior="history"
      screenOptions={{
        headerShown: false,
        drawerPosition: 'left',
        drawerType: 'front',
        swipeEdgeWidth: DRAWER_SWIPE_EDGE_WIDTH,
        swipeMinDistance: DRAWER_SWIPE_MIN_DISTANCE,
        drawerStyle: [styles.drawer, { width: useDrawerWidth() }],
        drawerItemStyle: styles.drawerItem,
        drawerLabelStyle: [
          styles.drawerLabel,
          {
            fontFamily: getFontFamily(fontFamily, 'Regular'),
            fontSize: drawerLabelFontSize,
          },
        ],
        drawerActiveBackgroundColor: 'transparent',
        drawerInactiveBackgroundColor: 'transparent',
        drawerActiveTintColor: menuColor,
        drawerInactiveTintColor: menuColor,
      }}
      drawerContent={props => (
        <CustomDrawerContent
          {...props}
          backgroundImageColor={menuBackgroundColor}
          menuLogo={
            (showMenuLogo && getUrlFromImageName(appConfig, menuLogo)) || undefined
          }
          aboutVisibility={appConfig.marqueBlancheOption !== 'YES'}
          settingsActive={
            (appConfig.cmpAvailable && appConfig.cmpOption) ||
            !!appConfig.privacyPolicyUrl ||
            !!appConfig.termsOfServiceUrl
          }
        />
      )}
    >
      {menuItems.map((menuItem, index) => (
        <Drawer.Screen
          initialParams={{ options: menuItem.options }}
          key={menuItem.name}
          name={menuItem.name}
          options={{
            drawerItemStyle: {
              ...styles.drawerItem,
              ...(isLastIndex(menuItems, index) ? styles.drawerItemLastChild : {}),
            },
            drawerLabel: menuItem.label,
            drawerIcon: props => (
              <CustomDrawerIcon
                {...props}
                icon={menuItem.icon}
                color={menuColor}
                accessibilityLabel={'Tab' + indexOf(menuItems, menuItem)}
                testID={'Tab' + indexOf(menuItems, menuItem)}
                faIcon={menuItem.faIcon}
              />
            ),
          }}
          getComponent={() => menuItem.component}
        />
      ))}
      <Drawer.Screen
        name="Settings"
        component={SettingsScreen}
        options={{ drawerItemStyle: { display: 'none' } }}
      />
    </Drawer.Navigator>
  );
};

const styles = StyleSheet.create({
  drawer: {
    backgroundColor: 'transparent',
  },
  drawerContentContainer: {
    paddingTop: mvs(10),
    marginHorizontal: mvs(24),
  },
  drawerItem: {
    marginHorizontal: 0,
    marginVertical: 0,
    borderBottomWidth: 1,
    borderBottomColor: '#d9d9d9',
  },
  drawerItemLastChild: {
    borderBottomWidth: 0,
  },
  drawerLabel: {
    marginLeft: -32 + mvs(15), // Left margin of 32 is hardcoded in react-navigation
  },
  container: {
    flex: 1,
    paddingTop: 20,
  },
  menuLogo: {
    marginTop: mvs(16),
    marginHorizontal: mvs(24),
    aspectRatio: 552 / 248, // Ratio connu de l'image reçu
  },
  safeAreaContainer: {
    flex: 1,
    backgroundColor: '#F9F9F9',
  },
});
